import { useEffect, useState } from "react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { SequenceActivity, SequenceEnrollmentRow } from "./assetActivity";

/**
 * Live enrollment reads for the marketing builders (round 43) - the real
 * automation_enrollments rows the Phase-4 worker writes, joined with
 * contact names. Returns null where the table is unreachable (demo
 * preview), so callers fall back to the simulation.
 */

interface RawEnrollment {
  id: string;
  definition_id: string;
  contact_id: number;
  status: "active" | "completed" | "failed" | "unenrolled";
  enrolled_at: string;
  current_step: number;
  last_error: string | null;
}

export interface LiveRunRow {
  id: string;
  definitionId: string;
  contactId: number;
  name: string;
  status: RawEnrollment["status"];
  enrolledAt: string;
  currentStep: number;
  lastError?: string;
}

const namesFor = async (ids: number[]): Promise<Map<number, string>> => {
  const map = new Map<number, string>();
  if (ids.length === 0) return map;
  const { data } = await getSupabaseClient()
    .from("contacts")
    .select("id, first_name, last_name")
    .in("id", [...new Set(ids)]);
  for (const c of (data ?? []) as { id: number; first_name?: string; last_name?: string }[]) {
    map.set(c.id, [c.first_name, c.last_name].filter(Boolean).join(" ") || `#${c.id}`);
  }
  return map;
};

/** All recent enrollments of one kind, newest first; null = live data unavailable. */
export const useLiveRuns = (
  kind: "workflow" | "sequence",
  definitionId?: string,
): LiveRunRow[] | null => {
  const [rows, setRows] = useState<LiveRunRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        let q = getSupabaseClient()
          .from("automation_enrollments")
          .select("id, definition_id, contact_id, status, enrolled_at, current_step, last_error")
          .eq("kind", kind)
          .order("enrolled_at", { ascending: false })
          .limit(200);
        if (definitionId) q = q.eq("definition_id", definitionId);
        const { data, error } = await q;
        if (error) throw new Error(error.message);
        const raw = (data ?? []) as RawEnrollment[];
        const names = await namesFor(raw.map((r) => r.contact_id));
        if (cancelled) return;
        setRows(
          raw.map((r) => ({
            id: r.id,
            definitionId: r.definition_id,
            contactId: r.contact_id,
            name: names.get(r.contact_id) ?? `#${r.contact_id}`,
            status: r.status,
            enrolledAt: r.enrolled_at,
            currentStep: r.current_step,
            lastError: r.last_error ?? undefined,
          })),
        );
      } catch {
        if (!cancelled) setRows(null); // demo preview or pre-automation workspace
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, definitionId]);

  return rows;
};

/** Live rows shaped like the sequence tab's activity model. */
export const toSequenceActivity = (
  rows: LiveRunRow[],
  stepCount: number,
): SequenceActivity => {
  const mapped: SequenceEnrollmentRow[] = rows.map((r) => ({
    contactId: r.contactId,
    name: r.name,
    status: r.status,
    stepIndex: Math.min(r.currentStep, Math.max(stepCount - 1, 0)),
    enrolledAt: r.enrolledAt,
  }));
  return {
    rows: mapped,
    active: mapped.filter((r) => r.status === "active").length,
    completed: mapped.filter((r) => r.status === "completed").length,
    replied: mapped.filter((r) => r.status === "unenrolled").length,
  };
};
